import { JSX, useEffect, useRef, useState } from 'react';
import {
  ImageField,
  RichTextField,
  RichText,
} from '@sitecore-jss/sitecore-jss-nextjs';
import QuoteChild from 'components/molecules/QuoteChild';
import Swiper from 'components/r2/Swiper';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';

type Quote = {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    image: ImageField;
    person: {
      value: string;
    };
    quotetext: RichTextField;
  };
};

type QuoteParentProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    description: RichTextField;
    title: {
      value: string;
    };
    backgroundcolor: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    quotechilditems: Array<Quote>;
  };
  rendering: {
    uid: string;
  };
};

const QuoteParent = (props: QuoteParentProps): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );
  const [index, setIndex] = useState(0);
  const [swipeLeft, setSwipeLeft] = useState(false);
  const [swipeRight, setSwipeRight] = useState(false);
  const [looping, setLooping] = useState(false);
  const { title, description, backgroundcolor, quotechilditems } = props.fields;
  const { uid } = props.rendering;
  const touchStart = useRef(0);
  const touchEnd = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEnd.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const minSwipe = 50;
    if (
      touchStart.current - touchEnd.current > minSwipe &&
      index < quotechilditems.length - 1
    ) {
      setSwipeLeft(true);
      setTimeout(() => {
        setSwipeLeft(false);
      }, 200);
      setIndex(index + 1);
    } else if (touchEnd.current - touchStart.current > minSwipe && index > 0) {
      setSwipeRight(true);
      setTimeout(() => {
        setSwipeRight(false);
      }, 200);
      setIndex(index - 1);
    } else if (
      touchStart.current - touchEnd.current > minSwipe &&
      index == quotechilditems.length - 1
    ) {
      setLooping(true);
      setSwipeLeft(true);
      setTimeout(() => {
        setSwipeLeft(false);
      }, 200);
      setIndex(0);
    }
  };

  const handleLeft = (newIndex?: number) => {
    setSwipeLeft(true);
    setTimeout(() => {
      setSwipeLeft(false);
    }, 200);
    newIndex && setLooping(true);
    newIndex ? setIndex(0) : setIndex(index + 1);
  };

  const handleRight = () => {
    setSwipeRight(true);
    setTimeout(() => {
      setSwipeRight(false);
    }, 200);
    setIndex(index - 1);
  };

  useEffect(() => {
    setLooping(false);
  }, [index]);

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`quote-parent-${props.rendering?.uid}`}
    >
      <div className="jss-inner-container">
        <div
          className={`quote-parent ${backgroundcolor.fields?.heading?.value.toLowerCase()}`}
          key={`quote-${uid}`}
        >
          {title?.value && (
            <h2 className="heading text-center">{title.value}</h2>
          )}
          <RichText
            field={description}
            className="osf-body-copy text-center body"
          />
          <div className="wrapper">
            <div className="swiper-container">
              {quotechilditems &&
                quotechilditems.map((quotechild, quoteindex) => {
                  return (
                    <QuoteChild
                      swipeLeft={swipeLeft}
                      swipeRight={swipeRight}
                      backgroundcolor={backgroundcolor.fields.heading.value}
                      quote={quotechild}
                      handleTouchStart={handleTouchStart}
                      handleTouchEnd={handleTouchEnd}
                      current={quoteindex == index}
                      prev={quoteindex == index - 1}
                      next={quoteindex == index + 1}
                      index={quoteindex - index}
                      quoteNum={quoteindex}
                      uid={uid}
                      key={`${uid}-${quoteindex}`}
                      firstIndex={quoteindex - index - quoteindex}
                      lastIndex={
                        quotechilditems.length -
                        1 +
                        (quoteindex - index - quoteindex)
                      }
                      numQuotes={quotechilditems.length}
                      looping={looping}
                    />
                  );
                })}
            </div>
            {quotechilditems.length > 1 && (
              <Swiper
                numberOfItems={quotechilditems.length}
                index={index}
                setIndex={setIndex}
                backgroundcolor={backgroundcolor.fields.heading.value}
                handleLeft={handleLeft}
                handleRight={handleRight}
                setLooping={setLooping}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteParent;
